import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, CheckCircle, Loader2, ChevronDown } from "lucide-react";
import Header from "../components/Header";
import ChatBot from "../components/ChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ülke kodları listesi
const countryCodes = [
  { code: "+90", country: "TR", flag: "🇹🇷", name: "Türkiye" },
  { code: "+49", country: "DE", flag: "🇩🇪", name: "Almanya" },
  { code: "+31", country: "NL", flag: "🇳🇱", name: "Hollanda" },
  { code: "+32", country: "BE", flag: "🇧🇪", name: "Belçika" },
  { code: "+33", country: "FR", flag: "🇫🇷", name: "Fransa" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "İngiltere" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "ABD" },
  { code: "+43", country: "AT", flag: "🇦🇹", name: "Avusturya" },
  { code: "+41", country: "CH", flag: "🇨🇭", name: "İsviçre" },
  { code: "+46", country: "SE", flag: "🇸🇪", name: "İsveç" },
  { code: "+47", country: "NO", flag: "🇳🇴", name: "Norveç" },
  { code: "+45", country: "DK", flag: "🇩🇰", name: "Danimarka" },
  { code: "+39", country: "IT", flag: "🇮🇹", name: "İtalya" },
  { code: "+34", country: "ES", flag: "🇪🇸", name: "İspanya" },
  { code: "+30", country: "GR", flag: "🇬🇷", name: "Yunanistan" },
  { code: "+7", country: "RU", flag: "🇷🇺", name: "Rusya" },
  { code: "+994", country: "AZ", flag: "🇦🇿", name: "Azerbaycan" },
  { code: "+993", country: "TM", flag: "🇹🇲", name: "Türkmenistan" },
  { code: "+998", country: "UZ", flag: "🇺🇿", name: "Özbekistan" },
  { code: "+996", country: "KG", flag: "🇰🇬", name: "Kırgızistan" },
  { code: "+7", country: "KZ", flag: "🇰🇿", name: "Kazakistan" },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    clinic_name: "",
    email: "",
    phone: "",
    service_type: "Bilgi Talebi",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]); // Türkiye varsayılan
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [content, setContent] = useState({
    contact_title: "Bize Ulaşın",
    contact_description: "Teklif almak veya sorularınız için bizimle iletişime geçin.",
    contact_hero_image: "",
    contact_phone: "+90 532 394 83 29",
    contact_email: "info@hisardentallab.com",
    contact_address: "İstanbul, Bağcılar",
    contact_hours: "Pazartesi - Cumartesi: 09:00 - 18:00"
  });
  
  // SMS verification states
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const countryDropdownRef = useRef(null);

  // Fetch content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };
    fetchContent();
    if (window.location.hash === '#iletisim-formu') {
      setTimeout(() => {
        document.getElementById('iletisim-formu')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, []);

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const serviceTypes = [
    "Zirkonyum Destekli Çalışmalar",
    "Estetik Çözümler (E.max)",
    "İmplant Üstü Protezler",
    "Geleneksel Protez ve Plaklar",
    "Diğer",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Reset phone verification if phone number changes
    if (name === "phone") {
      setPhoneVerified(false);
      setOtpSent(false);
      setVerificationCode("");
    }
  };

  // Format phone number with selected country code
  const formatPhoneNumber = (phone) => {
    let cleaned = phone.replace(/\D/g, '');
    // Remove leading zero if present
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    return selectedCountry.code + cleaned;
  };

  const handleSendOTP = async () => {
    if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) {
      toast.error("Lütfen geçerli bir telefon numarası girin");
      return;
    }
    
    setSendingOTP(true);
    try {
      const phoneNumber = formatPhoneNumber(formData.phone);
      await axios.post(`${API}/send-otp`, { phone_number: phoneNumber });
      setOtpSent(true);
      toast.success("Doğrulama kodu gönderildi!");
    } catch (error) {
      console.error("Send OTP error:", error);
      const detail = error?.response?.data?.detail || "Kod gönderilemedi. Lütfen tekrar deneyin.";
      toast.error(detail);
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error("Lütfen 6 haneli doğrulama kodunu girin");
      return;
    }
    
    setVerifyingOTP(true);
    try {
      const phoneNumber = formatPhoneNumber(formData.phone);
      const res = await axios.post(`${API}/verify-otp`, { phone_number: phoneNumber, code: verificationCode });
      if (res.data?.valid) {
        setPhoneVerified(true);
        toast.success("Telefon numarası doğrulandı!");
      } else {
        toast.error("Geçersiz kod. Lütfen tekrar deneyin.");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      const detail = error?.response?.data?.detail || "Doğrulama başarısız.";
      toast.error(detail);
    } finally {
      setVerifyingOTP(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      clinic_name: "",
      email: "",
      phone: "",
      service_type: "Bilgi Talebi",
      message: "",
    });
    setPhoneVerified(false);
    setOtpSent(false);
    setVerificationCode("");
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phoneVerified) {
      toast.error("Lütfen önce telefon numaranızı doğrulayın");
      return;
    }
    
    setLoading(true);

    try {
      const deviceInfo = navigator.userAgent;
      await axios.post(`${API}/quotes`, { ...formData, device_info: deviceInfo, phone_verified: true });
      toast.success("Teklif talebiniz başarıyla gönderildi!");
      // Formu yerinde sıfırlamak yerine temiz bir başarı ekranına geç
      // (in-place reconciliation kaynaklı removeChild çökmesini önler)
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#0d2341] to-[#0A192F]" />
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-300 rounded-full blur-3xl opacity-15" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left"
            >
              <p className="text-cyan-400 text-sm font-medium tracking-widest uppercase mb-4">
                İletişim
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                  {content.contact_title}
                </span>
              </h1>
              <p className="text-white/70 text-lg max-w-2xl">
                {content.contact_description}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src={getImageUrl(content.contact_hero_image)}
                alt="Dental Laboratuvar İletişim"
                className="rounded-2xl shadow-2xl w-full h-[350px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-bold text-[#0A192F] mb-8">
                İletişim Bilgilerimiz
              </h2>

              <div className="space-y-6 mb-10">
                <a
                  href={`tel:${content.contact_phone?.replace(/\s/g, '')}`}
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center text-cyan-500 group-hover:bg-gradient-to-br group-hover:from-cyan-500 group-hover:to-blue-500 group-hover:text-white transition-all">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Telefon</p>
                    <p className="font-medium text-[#0A192F]">{content.contact_phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${content.contact_email}`}
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center text-cyan-500 group-hover:bg-gradient-to-br group-hover:from-cyan-500 group-hover:to-blue-500 group-hover:text-white transition-all">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">E-posta</p>
                    <p className="font-medium text-[#0A192F]">{content.contact_email}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center text-cyan-500">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Konum</p>
                    <p className="font-medium text-[#0A192F]">
                      {content.contact_address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center text-cyan-500">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Çalışma Saatleri</p>
                    <p className="font-medium text-[#0A192F]">{content.contact_hours}</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <button
                onClick={() => {
                  const phoneNumber = content.contact_phone?.replace(/\s/g, '').replace('+', '');
                  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                  const message = encodeURIComponent("Merhaba size hisardentallab.com üzerinden ulaşıyorum. Bilgi almak istiyorum.");
                  if (isMobile) {
                    window.location.href = `whatsapp://send?phone=${phoneNumber}&text=${message}`;
                  } else {
                    window.open(`https://web.whatsapp.com/send?phone=${phoneNumber}&text=${message}`, '_blank');
                  }
                }}
                className="flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 rounded-xl hover:bg-[#20bd5a] transition-colors w-fit"
                data-testid="contact-whatsapp"
              >
                <MessageCircle className="w-6 h-6" fill="white" />
                <span className="font-medium">WhatsApp ile Yazın</span>
              </button>

              {/* Map */}
              <div className="mt-10">
                <iframe
                  src="https://maps.google.com/maps?q=Ba%C4%9Fc%C4%B1lar%2C%20%C4%B0stanbul&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-xl"
                  title="Hisar Dental Lab Konum"
                  data-testid="contact-map"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 rounded-2xl p-8 md:p-10">
                {submitted ? (
                  <div className="text-center py-8" data-testid="quote-success">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-9 h-9 text-green-500" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-3">
                      Talebiniz Alındı!
                    </h2>
                    <p className="text-slate-500 mb-8">
                      Teklif talebiniz başarıyla gönderildi. Ekibimiz en kısa sürede sizinle iletişime geçecek.
                    </p>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-8 py-3 font-medium shadow-lg shadow-cyan-500/25 transition-all duration-300"
                      data-testid="new-quote-btn"
                    >
                      Yeni Talep Gönder
                    </button>
                  </div>
                ) : (
                <>
                <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-2">
                  Teklif Formu
                </h2>
                <p className="text-slate-500 mb-8">
                  Formu doldurun, size en kısa sürede dönüş yapalım.
                </p>

                <form id="iletisim-formu" onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                  <div>
                    <label className="block text-sm font-medium text-[#0A192F] mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 rounded-lg px-4 py-3 outline-none transition-all"
                      placeholder="Adınız Soyadınız"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A192F] mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-white border border-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 rounded-lg px-4 py-3 outline-none transition-all"
                        placeholder="ornek@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A192F] mb-2">
                        Telefon * {phoneVerified && <CheckCircle className="inline w-4 h-4 text-green-500 ml-1" />}
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {/* Ülke Kodu Seçici */}
                        <div className="relative" ref={countryDropdownRef}>
                          <button
                            type="button"
                            onClick={() => !phoneVerified && setShowCountryDropdown(!showCountryDropdown)}
                            disabled={phoneVerified}
                            className={`flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-3 py-3 min-w-[90px] ${phoneVerified ? 'bg-green-50 border-green-300' : 'hover:border-cyan-500'}`}
                            data-testid="country-selector"
                          >
                            <span className="text-base">{selectedCountry.flag}</span>
                            <span className="text-sm font-medium">{selectedCountry.code}</span>
                            <ChevronDown className="w-3 h-3 text-slate-400" />
                          </button>
                          
                          {/* Dropdown */}
                          {showCountryDropdown && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto w-56">
                              {countryCodes.map((country) => (
                                <button
                                  key={country.code + country.country}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCountry(country);
                                    setShowCountryDropdown(false);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-cyan-50 text-left"
                                >
                                  <span className="text-lg">{country.flag}</span>
                                  <span className="text-sm">{country.name}</span>
                                  <span className="text-sm text-slate-400 ml-auto">{country.code}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-1 gap-2">
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            disabled={phoneVerified}
                            className={`flex-1 min-w-0 bg-white border border-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 rounded-lg px-3 py-3 outline-none transition-all text-sm ${phoneVerified ? 'bg-green-50 border-green-300' : ''}`}
                            placeholder="532 XXX XX XX"
                            data-testid="input-phone"
                          />
                          {!phoneVerified && !otpSent && (
                            <button
                              type="button"
                              onClick={handleSendOTP}
                              disabled={sendingOTP || !formData.phone}
                              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-3 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap text-sm"
                              data-testid="send-otp-btn"
                            >
                              {sendingOTP ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kod Gönder"}
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* OTP Input Section */}
                      {otpSent && !phoneVerified && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 p-4 bg-cyan-50 rounded-lg border border-cyan-200"
                        >
                          <p className="text-sm text-cyan-700 mb-2">
                            Telefonunuza gelen 6 haneli kodu girin:
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={verificationCode}
                              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="000000"
                              className="flex-1 bg-white border border-cyan-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 rounded-lg px-4 py-2 outline-none text-center text-lg tracking-widest"
                              data-testid="otp-input"
                            />
                            <button
                              type="button"
                              onClick={handleVerifyOTP}
                              disabled={verifyingOTP || verificationCode.length < 6}
                              className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
                              data-testid="verify-otp-btn"
                            >
                              {verifyingOTP ? <Loader2 className="w-5 h-5 animate-spin" /> : "Doğrula"}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={handleSendOTP}
                            disabled={sendingOTP}
                            className="text-cyan-600 text-sm mt-2 hover:underline"
                            data-testid="resend-otp-btn"
                          >
                            Kodu tekrar gönder
                          </button>
                        </motion.div>
                      )}
                      
                      {phoneVerified && (
                        <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Telefon numarası doğrulandı
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A192F] mb-2">
                      Mesajınız *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full bg-white border border-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 rounded-lg px-4 py-3 outline-none transition-all resize-none"
                      placeholder="İhtiyacınızı detaylı açıklayın..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !phoneVerified}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-8 py-4 font-medium shadow-lg shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-quote-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Teklif Talep Et
                      </>
                    )}
                  </button>
                </form>
                </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ChatBot />
    </div>
  );
};

export default ContactPage;
